import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const NAV_MODULES = [
  {
    key: 'requirements',
    title: 'Requirements',
    desc: 'Capture, organise and track functional requirements',
    icon: '📋',
    iconBg: '#ede9fe',
    iconColor: '#7c3aed',
    href: (pk) => `/projects/${pk}/requirements`,
  },
  {
    key: 'testcases',
    title: 'Test Cases',
    desc: 'Write and manage manual & automated test cases',
    icon: '✅',
    iconBg: '#dcfce7',
    iconColor: '#16a34a',
    href: (pk) => `/projects/${pk}/testcases`,
  },
  {
    key: 'testsuites',
    title: 'Test Suites',
    desc: 'Group test cases into logical suites for execution',
    icon: '📦',
    iconBg: '#fef3c7',
    iconColor: '#d97706',
    href: (pk) => `/projects/${pk}/test-suites`,
  },
  {
    key: 'runs',
    title: 'Test Runs',
    desc: 'Execute suites, track progress and view results',
    icon: '▶️',
    iconBg: '#e0f2fe',
    iconColor: '#0284c7',
    href: (pk) => `/runs?projectKey=${pk}`,
  },
  {
    key: 'jira',
    title: 'JIRA Integration',
    desc: 'Link issues, file bugs and sync status with JIRA',
    icon: '🔗',
    iconBg: '#dbeafe',
    iconColor: '#1d4ed8',
    href: (pk, id) => `/jira?projectId=${id}`,
  },
  {
    key: 'settings',
    title: 'Settings',
    desc: 'Configure custom fields, integrations and permissions',
    icon: '⚙️',
    iconBg: '#f1f5f9',
    iconColor: '#475569',
    href: (pk) => `/projects/${pk}/settings`,
  },
];

function ProjectDetail() {
  const { projectKey: projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProject(data.data || data);
      } else if (response.status === 401) {
        navigate('/login');
      }
    } catch (err) {
      console.error('Error loading project:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <div><h1 className="page-title">Project Dashboard</h1></div>
        </div>
        <div className="zq-spinner-wrap">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div>
        <div className="page-header">
          <div><h1 className="page-title">Project not found</h1></div>
        </div>
        <div className="container-fluid">
          <div className="alert alert-danger">Project not found.</div>
          <Link to="/projects" className="btn btn-outline-secondary">← Back to Projects</Link>
        </div>
      </div>
    );
  }

  const pk = projectId;

  return (
    <div>
      {/* ── Page header ── */}
      <div className="page-header">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><Link to="/projects">Projects</Link></li>
              <li className="breadcrumb-item active">{project.name}</li>
            </ol>
          </nav>
          <h1 className="page-title d-flex align-items-center gap-2">
            {project.name}
            <span
              style={{
                fontSize: '12px', fontWeight: 600,
                background: 'var(--zq-primary-light)', color: 'var(--zq-primary)',
                padding: '2px 8px', borderRadius: '5px', fontFamily: 'monospace',
              }}
            >
              {project.key}
            </span>
            <span className={`badge ${project.status === 'active' ? 'bg-success' : 'bg-warning'}`} style={{ fontSize: '11px' }}>
              {project.status}
            </span>
          </h1>
          {project.description && (
            <p className="page-subtitle">{project.description}</p>
          )}
        </div>
        <Link
          to={`/projects/${pk}/settings`}
          className="btn btn-outline-secondary btn-sm"
        >
          ⚙️ Settings
        </Link>
      </div>

      {/* ── Module grid ── */}
      <div className="container-fluid">
        <div className="row g-3">
          {NAV_MODULES.map(mod => (
            <div key={mod.key} className="col-md-6 col-lg-4">
              <Link
                to={mod.href(pk, projectId)}
                className="zq-nav-card text-decoration-none"
              >
                <div className="nav-card-body">
                  <div
                    className="nav-card-icon"
                    style={{ background: mod.iconBg }}
                  >
                    {mod.icon}
                  </div>
                  <div className="nav-card-title">{mod.title}</div>
                  <div className="nav-card-desc">{mod.desc}</div>
                </div>
                <div className="nav-card-footer">
                  Open {mod.title} →
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ProjectDetail;
